/**
 * Corrosion Analyser Vuex Module
 *
 * Manages state, getters, mutations, and actions for the Corrosion Analyser dashboard.
 * Handles AVA-Inspect tags filtered by corrosion defect type, grouped by master tags.
 *
 * Features:
 * - Fetch and filter AVA-Inspect tags with corrosion defects
 * - Group tags by master (Pipeline) tag
 * - Integrate with 3D/2D/360° viewer
 * - Support search, filtering, and pagination
 * - Calculate statistics (critical count, average severity, etc.)
 */

import avaInspectService from '@/features/Tags/services/avaInspect.service';
import corrosionAnalyserService from '../service/corrosionAnalyser.service';
import store from '@/store';
import { ForgeAvaInspect } from '@/scripts/js/forgeModels';
import { error } from '@/utils/debugLogger';

const state = {
  entries: [],
  total: 0,
  loading: false,
  filters: {},
  options: {
    page: 1,
    itemsPerPage: 10,
    sortBy: [],
    sortDesc: [],
    search: '',
    filters: {
      status: [],
      severity: [],
      priority: [],
      defectType: [],
      analyzerMode: [],
      analyzerStatus: [],
      pipelineTags: [],
      plant: [],
      pltName: [],
      unit: [],
      funcLocation: [],
      area: [],
      system: [],
      subSystem: [],
      level2D: [],
      processFunction: [],
      lno: [],
      fireZone: [],
      discipline: [],
      pipeSpecification: [],
      fluidCode: [],
      fluidDescription: []
    },
    facilityId: null
  },
  viewerOpened: false,
  viewerTab: '3D View',
  isolatedTags: [],
  processingCount: 0
};

const getters = {
  GET_ENTRIES: state => state.entries,
  GET_TOTAL: state => state.total,
  GET_LOADING: state => state.loading,
  GET_OPTIONS: state => state.options,
  GET_FILTERS: state => state.filters,
  VIEWER_OPENED: state => state.viewerOpened,
  VIEWER_TAB: state => state.viewerTab,
  ISOLATED_TAGS: state => state.isolatedTags,

  /**
   * Group entries by master tag for hierarchical display
   * Returns object with masterTagName as key and array of tags as value
   */
  GET_GROUPED_ENTRIES: state => {
    const grouped = {};

    state.entries.forEach(tag => {
      const masterTagName = tag.masterTag?.name || tag.masterTagName || 'Unlinked';
      const masterTagId = tag.masterTag?.id || tag.masterTagId || 'unlinked';

      if (!grouped[masterTagName]) {
        grouped[masterTagName] = {
          masterTagId,
          masterTagName,
          masterTag: tag.masterTag,
          tags: [],
          statistics: {
            total: 0,
            critical: 0,
            high: 0,
            medium: 0,
            low: 0
          }
        };
      }

      grouped[masterTagName].tags.push(tag);
      grouped[masterTagName].statistics.total++;

      // Count by severity
      if (tag.severity === 'Critical') {
        grouped[masterTagName].statistics.critical++;
      } else if (tag.severity === 'High') {
        grouped[masterTagName].statistics.high++;
      } else if (tag.severity === 'Medium') {
        grouped[masterTagName].statistics.medium++;
      } else if (tag.severity === 'Low') {
        grouped[masterTagName].statistics.low++;
      }
    });

    return grouped;
  },

  /**
   * Get summary statistics for all corrosion tags
   */
  GET_STATISTICS: state => {
    const stats = {
      totalTags: state.entries.length,
      criticalCount: 0,
      highCount: 0,
      mediumCount: 0,
      lowCount: 0,
      masterTagCount: 0,
      averagePredictedLoss: 0
    };

    const masterTags = new Set();
    let totalPredictedLoss = 0;
    let lossCount = 0;

    state.entries.forEach(tag => {
      // Count by severity
      if (tag.severity === 'Critical') stats.criticalCount++;
      else if (tag.severity === 'High') stats.highCount++;
      else if (tag.severity === 'Medium') stats.mediumCount++;
      else if (tag.severity === 'Low') stats.lowCount++;

      // Track master tags
      if (tag.masterTagId) {
        masterTags.add(tag.masterTagId);
      }

      // Calculate average predicted loss
      if (tag.predictedWtLoss && !isNaN(parseFloat(tag.predictedWtLoss))) {
        totalPredictedLoss += parseFloat(tag.predictedWtLoss);
        lossCount++;
      }
    });

    stats.masterTagCount = masterTags.size;
    stats.averagePredictedLoss = lossCount > 0 ? (totalPredictedLoss / lossCount).toFixed(2) : 0;

    return stats;
  }
};

const mutations = {
  SET_ENTRIES(state, entries) {
    state.entries = entries;
  },

  SET_TOTAL(state, total) {
    state.total = total;
  },

  SET_LOADING(state, loading) {
    state.loading = loading;
  },

  SET_FILTERS(state, filters) {
    state.filters = filters;
  },

  SET_OPTIONS(state, options) {
    state.options = { ...state.options, ...options };
  },

  SET_VIEWER_OPENED(state, opened) {
    state.viewerOpened = opened;
  },

  SET_VIEWER_TAB(state, tab) {
    state.viewerTab = tab;
  },

  SET_ISOLATED_TAGS(state, tags) {
    state.isolatedTags = tags;
  },

  SET_PROCESSING_COUNT(state, count) {
    state.processingCount = count;
  },

  UPDATE_TAG(state, updatedTag) {
    const index = state.entries.findIndex(tag => tag.id === updatedTag.id);
    if (index !== -1) {
      state.entries.splice(index, 1, updatedTag);
    }
  },

  REMOVE_TAG(state, tagId) {
    const index = state.entries.findIndex(tag => tag.id === tagId);
    if (index !== -1) {
      state.entries.splice(index, 1);
      state.total--;
    }
  }
};

const actions = {
  /**
   * Get available filter options
   */
  async GET_FILTERS({ commit, state }, payload) {
    try {
      const response = await corrosionAnalyserService.getFilters(payload);
      commit('SET_FILTERS', response.data);
      return response.data;
    } catch (error) {
      error('Error fetching filters:', error);
      commit('SET_FILTERS', {});
      throw error;
    }
  },

  /**
   * Fetch AVA-Inspect tags filtered for corrosion defects
   */
  async GET({ commit, state }) {
    commit('SET_LOADING', true);

    try {
      const params = {
        assetId: state.options.facilityId,
        search: state.options.search,
        page: state.options.page,
        itemsPerPage: state.options.itemsPerPage,
        sortBy: state.options.sortBy[0] || 'name',
        sortDesc: state.options.sortDesc[0] || false
      };

      // Apply all filters
      const filters = {};
      Object.keys(state.options.filters).forEach(key => {
        if (state.options.filters[key] && state.options.filters[key].length > 0) {
          filters[key] = state.options.filters[key];
        }
      });

      const searchQuery = {
        ...params,
        filters
      };

      const response = await corrosionAnalyserService.get(searchQuery);

      const items = response.data.items.map(t => {
        let instance = new ForgeAvaInspect();
        instance.setSameParameters(t);
        instance.parseContent();
        return instance;
      })

      commit('SET_ENTRIES', items || response.data || []);
      commit('SET_TOTAL', response.data.total || response.data.length || 0);
    } catch (error) {
      error('Error fetching corrosion analyser data:', error);
      commit('SET_ENTRIES', []);
      commit('SET_TOTAL', 0);
    } finally {
      commit('SET_LOADING', false);
    }
  },

  /**
   * Update a single AVA-Inspect tag
   */
  async UPDATE({ commit, dispatch }, tag) {
    try {
      const response = await avaInspectService.update({
        tag,
        thumbnail: null,
        cropVolume: null,
        propagateUpdates: true,
        assetId: store.state.assetId
      });

      let instance = new ForgeAvaInspect();
      instance.setSameParameters(response.data);
      instance.parseContent();

      commit('UPDATE_TAG', instance);
      return instance;
    } catch (error) {
      error('Error updating corrosion tag:', error);
      throw error;
    }
  },

  /**
   * Delete one or more tags
   */
  async DELETE({ commit, dispatch }, ids) {
    try {
      const idsArray = Array.isArray(ids) ? ids : [ids];
      await avaInspectService.del(idsArray);

      idsArray.forEach(id => {
        commit('REMOVE_TAG', id);
      });

      // Refresh the list
      dispatch('GET');
    } catch (error) {
      error('Error deleting corrosion tags:', error);
      throw error;
    }
  },

  /**
   * Delete multiple tags
   */
  async DELETE_MULTI({ dispatch }, ids) {
    return dispatch('DELETE', ids);
  },

  /**
   * Get count of tags being processed
   */
  async GET_PROCESSING_COUNT({ commit }, facilityId) {
    try {
      // Implement if backend supports processing status
      // For now, return 0
      commit('SET_PROCESSING_COUNT', 0);
      return 0;
    } catch (err) {
      error('Error getting processing count:', err);
      return 0;
    }
  },

  /**
   * Generate report for a tag
   */
  async GENERATE_REPORT({ rootState }, { id }) {
    try {
      // Implement report generation
      // This could call a backend endpoint or generate client-side
      const tag = await avaInspectService.getById(id, rootState?.assetId ?? null);
      return tag.data;
    } catch (err) {
      error('Error generating report:', err);
      throw err;
    }
  }
};

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions
};
